<template>
  <v-group :config="groupConfig">
    <v-rect v-if="debug" :config="rectConfig" />
    <v-text v-if="text" ref="text" :config="textConfig" />
  </v-group>
</template>

<script>
/**
 * @enum
 * @readonly
 */
import ConfiguratorTextureCanvasLayer from '../../mixins/ConfiguratorTextureCanvasLayer';
import { loadFont } from '../../utils/FontLoader';
import {
  GlassesGetter,
  prefixType as prefixGlassesType,
} from '../../store/modules/configurator/glasses';
import { getTypoName } from '../../utils/TypoUtils';

const TextAlign = Object.freeze({
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
});

export default {
  name: 'ConfiguratorTextureCanvasLayerText',
  mixins: [ConfiguratorTextureCanvasLayer],
  props: {
    text: {
      type: String,
      default: undefined,
    },
    align: {
      type: String,
      default: TextAlign.CENTER,
      validator(v) {
        return Object.values(TextAlign).includes(v);
      },
    },
    fontSize: {
      type: Number,
      default: undefined,
    },
    typoId: { type: Number, default: undefined },
    bold: { type: Boolean },
    italic: { type: Boolean },
    underlined: { type: Boolean },
    color: {
      type: String,
      default: undefined,
    },
    innerFontSize: { type: Number, default: undefined },
  },
  data() {
    return {
      fontPromise: null,
      isFontLoaded: false,
    };
  },
  computed: {
    textConfig() {
      const groupNodeCommonConfig = this.groupNodeCommonConfig;

      /** @type {TextConfig} */
      const config = {
        x: this.innerX || groupNodeCommonConfig.x,
        y: this.innerY || groupNodeCommonConfig.y,
        rotation: this.innerRotation || this.rotation || 0,
        text: this.text,
      };

      if (!this.innerX) {
        config.width = groupNodeCommonConfig.width;
      }

      if (!this.innerY) {
        config.height = groupNodeCommonConfig.height;
      }

      if (this.innerScaleX) {
        config.scaleX = this.innerScaleX;
      }

      if (this.innerScaleY) {
        config.scaleY = this.innerScaleY;
      }

      if (this.align) {
        config.align = this.align;
      }

      if (this.color) {
        config.fill = this.color;
      }

      if (this.fontSize) {
        config.fontSize = this.fontSize;
      }

      if (this.underlined) {
        config.textDecoration = 'underline';
      }

      if (this.italic) {
        config.fontStyle = 'italic';
      }

      if (this.isFontLoaded && this.fontFamily) {
        config.fontFamily = this.fontFamily;
      }

      return config;
    },
    /**
     *
     * @return {string|null}
     */
    fontFamily() {
      if (this.typoId) {
        const typo =
          (this.typoId &&
            this.$store.getters[
              prefixGlassesType(GlassesGetter.FIND_TYPO_BY_ID)
            ](this.typoId)) ||
          undefined;

        if (typo) {
          return getTypoName(typo, this.bold);
        }
      }

      return undefined;
    },
  },
  created() {
    this.$watch(
      'fontFamily',
      async function (typoName) {
        if (this.isFontLoaded) {
          this.isFontLoaded = false;
        }

        if (this.text && typoName) {
          // Set Promise before waiting for it so layerReady never returns null
          this.fontPromise = loadFont(typoName).catch(e => {
            console.error(`Unable to load font '${typoName}'`);
            console.error(e);
          });

          await this.fontPromise;
        } else {
          this.fontPromise = Promise.resolve();
        }

        this.isFontLoaded = true;
      },
      { immediate: true }
    );
  },
  methods: {
    /**
     *
     * @return {Promise<null>}
     */
    async layerReady() {
      // Wait for $nextTick so watchers are called before
      // I don't really understand why it works
      return this.$nextTick()
        .then(() => this.fontPromise)
        .then(() => {
          // Ensure image is rendered by requesting a canvas draw
          if (this.$refs.text) {
            const textNode = this.$refs.text.getNode();
            textNode.getLayer().batchDraw();
          }
        });
    },
  },
};
</script>
